import React, { useEffect, useMemo, useState, useCallback } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { SectionHeader } from "@/components/common/Widgets";
import { Sparkles, Download, RotateCcw, Loader2, Filter, Table as TableIcon, X, Settings2 } from "lucide-react";
import { fmtDate } from "@/lib/format";

export default function ReportsBuilderPage() {
  const [catalog, setCatalog] = useState(null);
  const [columns, setColumns] = useState([]);
  const [filters, setFilters] = useState({});
  const [dateField, setDateField] = useState("created_at");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [limit, setLimit] = useState(500);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [generatedAt, setGeneratedAt] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/reports/fields");
        setCatalog(res.data);
        setColumns(res.data?.defaults?.columns || []);
      } catch (e) {
        toast.error("Failed to load report fields");
      }
    })();
  }, []);

  const fieldsByKey = useMemo(() => {
    const m = {};
    (catalog?.fields || []).forEach((f) => { m[f.key] = f; });
    return m;
  }, [catalog]);

  const groupedFields = useMemo(() => {
    const groups = {};
    (catalog?.fields || []).forEach((f) => {
      groups[f.group] = groups[f.group] || [];
      groups[f.group].push(f);
    });
    return groups;
  }, [catalog]);

  const filterableFields = (catalog?.fields || []).filter((f) => f.filterable);
  const dateFields = (catalog?.fields || []).filter((f) => f.date);

  const toggleColumn = (key) => {
    setColumns((prev) => (prev.includes(key) ? prev.filter((c) => c !== key) : [...prev, key]));
  };

  const setFilter = (k, v) => setFilters((prev) => ({ ...prev, [k]: v }));
  const removeFilter = (k) => setFilters((prev) => {
    const next = { ...prev };
    delete next[k];
    return next;
  });

  const runReport = useCallback(async () => {
    if (columns.length === 0) return toast.error("Pick at least one column");
    setBusy(true);
    try {
      const cleanedFilters = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== "" && v !== null && v !== undefined && !(Array.isArray(v) && v.length === 0)) cleanedFilters[k] = v;
      });
      const res = await api.post("/reports/custom", {
        columns,
        filters: cleanedFilters,
        date_field: dateField,
        date_from: dateFrom || null,
        date_to: dateTo || null,
        sort_by: sortBy,
        sort_dir: sortDir,
        limit: Number(limit) || 500,
      });
      setRows(res.data?.rows || []);
      setGeneratedAt(res.data?.generated_at);
      toast.success(`Report generated · ${res.data?.count} rows`);
    } catch (e) {
      toast.error("Report failed");
    } finally {
      setBusy(false);
    }
  }, [columns, filters, dateField, dateFrom, dateTo, sortBy, sortDir, limit]);

  const downloadCsv = async () => {
    if (columns.length === 0) return toast.error("Pick at least one column");
    setDownloading(true);
    try {
      const cleanedFilters = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== "" && v !== null && v !== undefined && !(Array.isArray(v) && v.length === 0)) cleanedFilters[k] = v;
      });
      const token = localStorage.getItem("acas_token");
      const resp = await fetch(`${API}/reports/custom/export.csv`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          columns,
          filters: cleanedFilters,
          date_field: dateField,
          date_from: dateFrom || null,
          date_to: dateTo || null,
          sort_by: sortBy,
          sort_dir: sortDir,
          limit: Number(limit) || 500,
        }),
      });
      if (!resp.ok) throw new Error("CSV download failed");
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `acas_report_${new Date().toISOString().replace(/[:.]/g, "-")}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error("CSV download failed");
    } finally {
      setDownloading(false);
    }
  };

  const reset = () => {
    setColumns(catalog?.defaults?.columns || []);
    setFilters({});
    setDateFrom("");
    setDateTo("");
    setSortBy("created_at");
    setSortDir("desc");
    setLimit(500);
    setRows([]);
    setGeneratedAt(null);
  };

  if (!catalog) {
    return <div className="p-6 space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-12 w-full" />)}</div>;
  }

  const activeFilterEntries = Object.entries(filters).filter(([, v]) => v !== "" && v !== null && v !== undefined && !(Array.isArray(v) && v.length === 0));

  return (
    <div className="space-y-5" data-testid="reports-builder-page">
      <SectionHeader
        title="Custom Report Builder"
        subtitle="Pick the columns you need, add filters, and generate a tenant-scoped report. Export as CSV."
        action={(
          <div className="flex gap-2 flex-wrap">
            <Button variant="ghost" size="sm" onClick={reset} data-testid="report-reset"><RotateCcw className="h-4 w-4 mr-1" />Reset</Button>
            <Button variant="secondary" size="sm" onClick={downloadCsv} disabled={downloading || rows.length === 0} data-testid="report-download-csv">
              {downloading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}Download CSV
            </Button>
            <Button size="sm" onClick={runReport} disabled={busy} data-testid="report-run">
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}Generate Report
            </Button>
          </div>
        )}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Columns picker */}
        <Card className="p-4 lg:col-span-1">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold heading flex items-center gap-2"><Settings2 className="h-4 w-4" />Columns</h3>
            <Badge variant="outline" className="text-[10px]">{columns.length} selected</Badge>
          </div>
          <div className="space-y-3 max-h-[440px] overflow-y-auto pr-1">
            {Object.entries(groupedFields).map(([group, fields]) => (
              <div key={group}>
                <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-1.5">{group}</div>
                <div className="space-y-1.5">
                  {fields.map((f) => (
                    <label
                      key={f.key}
                      className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/40 rounded px-1.5 py-1"
                      data-testid={`report-col-${f.key}`}
                    >
                      <Checkbox
                        checked={columns.includes(f.key)}
                        onCheckedChange={() => toggleColumn(f.key)}
                      />
                      <span className="flex-1">{f.label}</span>
                      {f.numeric && <Badge variant="outline" className="text-[10px] text-info border-info/30">#</Badge>}
                      {f.date && <Badge variant="outline" className="text-[10px] text-accent border-accent/30">date</Badge>}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Filters + Date range + Sort */}
        <Card className="p-4 lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold heading flex items-center gap-2"><Filter className="h-4 w-4" />Filters & Sort</h3>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="secondary" size="sm" data-testid="report-add-filter"><Filter className="h-3.5 w-3.5 mr-1" />Add filter</Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-2" align="end">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5 px-1">Pick a field to filter on</div>
                <div className="space-y-0.5 max-h-72 overflow-y-auto">
                  {filterableFields.map((f) => (
                    <button
                      key={f.key}
                      type="button"
                      onClick={() => setFilter(f.key, "")}
                      className="w-full text-left px-2 py-1.5 rounded text-sm hover:bg-muted disabled:opacity-40"
                      disabled={Object.prototype.hasOwnProperty.call(filters, f.key)}
                      data-testid={`report-add-filter-${f.key}`}
                    >
                      <span>{f.label}</span>
                      <span className="text-[10px] text-muted-foreground ml-1">{f.group}</span>
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {activeFilterEntries.length === 0 ? (
            <div className="text-xs text-muted-foreground border border-dashed rounded-md p-4 text-center">
              No filters applied. Click <b>Add filter</b> to narrow your report.
            </div>
          ) : (
            <div className="space-y-2">
              {activeFilterEntries.map(([k, v]) => {
                const fld = fieldsByKey[k];
                const enumValues = (fld?.enum_source && catalog?.enums?.[fld.enum_source]) || null;
                return (
                  <div key={k} className="grid grid-cols-12 items-center gap-2">
                    <div className="col-span-4 text-xs uppercase tracking-wide font-semibold text-muted-foreground">{fld?.label || k}</div>
                    <div className="col-span-7">
                      {enumValues ? (
                        <Select value={String(v || "")} onValueChange={(val) => setFilter(k, val)}>
                          <SelectTrigger data-testid={`report-filter-${k}`}><SelectValue placeholder="—" /></SelectTrigger>
                          <SelectContent>{enumValues.map((opt) => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}</SelectContent>
                        </Select>
                      ) : (
                        <Input value={v ?? ""} onChange={(e) => setFilter(k, e.target.value)} placeholder="Value…" data-testid={`report-filter-${k}`} />
                      )}
                    </div>
                    <div className="col-span-1 flex justify-end">
                      <Button variant="ghost" size="icon" onClick={() => removeFilter(k)} data-testid={`report-remove-filter-${k}`}><X className="h-3.5 w-3.5" /></Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3 border-t border-border">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">Date field</Label>
              <Select value={dateField} onValueChange={setDateField}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{dateFields.map((f) => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">From</Label>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="report-date-from" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">To</Label>
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="report-date-to" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">Sort by</Label>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{(catalog.fields || []).filter((f) => !f.join).map((f) => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">Direction</Label>
                <Select value={sortDir} onValueChange={setSortDir}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="desc">Descending</SelectItem>
                    <SelectItem value="asc">Ascending</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">Limit</Label>
                <Input type="number" min={1} max={5000} value={limit} onChange={(e) => setLimit(e.target.value)} data-testid="report-limit" />
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Results */}
      <Card className="p-0 overflow-hidden" data-testid="report-results-card">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <TableIcon className="h-4 w-4 text-accent" />
            <span className="text-sm font-semibold heading">Results</span>
            <Badge variant="outline" className="text-[10px]">{rows.length} row{rows.length === 1 ? "" : "s"}</Badge>
          </div>
          {generatedAt && (
            <div className="text-[11px] text-muted-foreground mono">Generated {fmtDate(generatedAt)}</div>
          )}
        </div>
        {rows.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">
            <TableIcon className="h-10 w-10 mx-auto mb-2 text-muted-foreground/40" />
            <p className="text-sm">{busy ? "Generating…" : "No data. Pick columns, add filters, and click Generate Report."}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground sticky top-0">
                <tr>
                  {columns.map((c) => (
                    <th key={c} className={`text-left px-3 py-2.5 ${fieldsByKey[c]?.numeric ? "text-right" : ""}`}>
                      {fieldsByKey[c]?.label || c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-t border-border hover:bg-muted/30">
                    {columns.map((c) => {
                      const f = fieldsByKey[c];
                      const v = r[c];
                      const isDate = f?.date && typeof v === "string";
                      return (
                        <td
                          key={c}
                          className={`px-3 py-2 ${f?.numeric ? "num-right mono tabular" : ""} ${isDate ? "text-xs text-muted-foreground mono" : ""}`}
                        >
                          {v === null || v === undefined || v === "" ? "—" : isDate ? fmtDate(v) : typeof v === "boolean" ? (v ? "Yes" : "No") : String(v)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
